import { logViewjobDwellRest } from "./logViewjobDwellRest";
import { useIsDesktopDisplay } from "./ViewJobDisplayContext";
import { logTapButtonWithExtraParams } from "../../../shared/native-modules/tare/logTapButtonWithExtraParams";

import {
  AvroOptional,
  log as logMobviewjobDwell,
} from "@indeed/signals-schema/com/indeed/signals/schema/jobsearch/mobviewjobDwell";
import React, {
  createContext,
  useContext,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { AppState, type AppStateStatus } from "react-native";

interface DwellTimingContextValue {
  markApplyStart: () => void;
  handleScroll: (scrollY: number, contentHeight: number, scrollViewHeight: number) => void;
  setSectionVisibility: (section: DwellSectionKey, isVisible: boolean) => void;
}

const DwellTimingContext = createContext<DwellTimingContextValue | null>(null);

export type DwellSectionKey =
  | "pay"
  | "benefits"
  | "jobType"
  | "location"
  | "matchOverview"
  | "fullJobDescription"
  | "otherJobRecommendations"
  | "cta";

type DwellSectionDurations = Record<DwellSectionKey, number>;
type DwellSectionCounts = Record<DwellSectionKey, number>;
type DwellSectionStarts = Record<DwellSectionKey, number | null>;

const SECTION_KEYS: DwellSectionKey[] = [
  "pay",
  "benefits",
  "jobType",
  "location",
  "matchOverview",
  "fullJobDescription",
  "otherJobRecommendations",
  "cta",
];

function createSectionRecord<T>(initialValue: T): Record<DwellSectionKey, T> {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return Object.fromEntries(SECTION_KEYS.map((section) => [section, initialValue])) as Record<
    DwellSectionKey,
    T
  >;
}

function createEmptyDurations(): DwellSectionDurations {
  return createSectionRecord(0);
}

function createEmptySectionCounts(): DwellSectionCounts {
  return createSectionRecord(0);
}

function createEmptySectionStarts(): DwellSectionStarts {
  return createSectionRecord<number | null>(null);
}

export interface DwellTimingProviderProps {
  children: ReactNode;
  /** The Page Tracking Key, i.e. the mobtk */
  tk?: string | null;
  /** The page type used for dwell attribution */
  pgtype?: string | null;
  /** The encoded account key */
  accountId?: string | null;
  /** The job key */
  jobKey?: string | null;
  /** Whether the viewed job has an AI company summary */
  hasCompanySummary?: boolean | null;
}

export function DwellTimingProvider({
  children,
  tk,
  pgtype,
  accountId,
  jobKey,
  hasCompanySummary,
}: DwellTimingProviderProps) {
  const isDesktopDisplay = useIsDesktopDisplay();
  const startTimeRef = useRef<number | null>(null);
  const hasApplyStartRef = useRef<boolean>(false);
  const hasLoggedRef = useRef<boolean>(false);
  const maxScrollPercentageRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionDurationsRef = useRef<DwellSectionDurations>(createEmptyDurations());
  const sectionVisibleCountsRef = useRef<DwellSectionCounts>(createEmptySectionCounts());
  const sectionVisibleStartRef = useRef<DwellSectionStarts>(createEmptySectionStarts());

  const getSectionDurations = useCallback((now: number): DwellSectionDurations => {
    const durations: DwellSectionDurations = { ...sectionDurationsRef.current };

    for (const section of SECTION_KEYS) {
      const visibleStart = sectionVisibleStartRef.current[section];
      if (visibleStart !== null) {
        durations[section] += now - visibleStart;
      }
    }

    return durations;
  }, []);

  const setSectionVisibility = useCallback((section: DwellSectionKey, isVisible: boolean) => {
    if (hasLoggedRef.current) {
      return;
    }

    const now = Date.now();
    const currentCount = sectionVisibleCountsRef.current[section];

    if (isVisible) {
      const nextCount = currentCount + 1;
      sectionVisibleCountsRef.current[section] = nextCount;
      if (nextCount === 1) {
        sectionVisibleStartRef.current[section] = now;
      }
      return;
    }

    if (currentCount <= 0) {
      return;
    }

    const nextCount = currentCount - 1;
    sectionVisibleCountsRef.current[section] = nextCount;

    if (nextCount === 0) {
      const visibleStart = sectionVisibleStartRef.current[section];
      if (visibleStart !== null) {
        sectionDurationsRef.current[section] += now - visibleStart;
        sectionVisibleStartRef.current[section] = null;
      }
    }
  }, []);

  const logDwellTime = useCallback(() => {
    // Ensure we only log once per page load
    if (hasLoggedRef.current || startTimeRef.current === null) {
      return;
    }

    const endTime = Date.now();
    const duration = endTime - startTimeRef.current;

    // Only log if the user spent at least 100ms on the page
    if (duration >= 100) {
      hasLoggedRef.current = true;

      if (isDesktopDisplay) {
        logViewjobDwellRest({
          tk,
          duration,
          endTime,
          hasApplyStart: hasApplyStartRef.current,
          accountKey: accountId,
          jobKey,
        });
        return;
      }

      const sectionDurations = getSectionDurations(endTime);
      logMobviewjobDwell({
        tk: AvroOptional.string(tk),
        pgtype: { string: pgtype || "mobvj" },
        duration: { long: duration },
        end: { long: endTime },
        hasApplyStart: { boolean: hasApplyStartRef.current },
        scrollPercentage: { long: maxScrollPercentageRef.current },
        accountId: AvroOptional.string(accountId),
        jobKey: AvroOptional.string(jobKey),
        pay_in_view_ms: { long: sectionDurations.pay },
        benefits_in_view_ms: { long: sectionDurations.benefits },
        job_type_in_view_ms: { long: sectionDurations.jobType },
        location_in_view_ms: { long: sectionDurations.location },
        match_overview_in_view_ms: { long: sectionDurations.matchOverview },
        full_job_description_in_view_ms: { long: sectionDurations.fullJobDescription },
        other_job_recommendations_in_view_ms: {
          long: sectionDurations.otherJobRecommendations,
        },
        cta_in_view_ms: { long: sectionDurations.cta },
      });
    }
  }, [accountId, getSectionDurations, isDesktopDisplay, jobKey, pgtype, tk]);

  // Start dwell timing when hook mounts
  useEffect(() => {
    startTimeRef.current = Date.now();
    hasApplyStartRef.current = false;
    hasLoggedRef.current = false;
    maxScrollPercentageRef.current = 0;
    sectionDurationsRef.current = createEmptyDurations();
    sectionVisibleCountsRef.current = createEmptySectionCounts();
    sectionVisibleStartRef.current = createEmptySectionStarts();

    return () => {
      // Log dwell time on unmount if not already logged
      if (!hasLoggedRef.current) {
        logDwellTime();
      }
      // Clean up scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [logDwellTime]);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === "background" && !hasLoggedRef.current) {
        logDwellTime();
      }
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription?.remove();
  }, [logDwellTime]);

  const markApplyStart = useCallback(() => {
    hasApplyStartRef.current = true;
    logTapButtonWithExtraParams("rn-view-job", "apply-start", ["viewjob"], {
      hasCompanySummary: hasCompanySummary ?? false,
    }).catch(console.error);
    // Log immediately when apply is clicked
    logDwellTime();
  }, [logDwellTime, hasCompanySummary]);

  const handleScroll = useCallback(
    (scrollY: number, contentHeight: number, scrollViewHeight: number) => {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set a new timeout to debounce the update
      scrollTimeoutRef.current = setTimeout(() => {
        // Calculate scroll percentage
        const maxScrollY = Math.max(0, contentHeight - scrollViewHeight);
        const percentage =
          maxScrollY > 0 ? Math.min(100, Math.max(0, (scrollY / maxScrollY) * 100)) : 0;

        // Only update if the new percentage is higher than the current max
        if (percentage > maxScrollPercentageRef.current) {
          maxScrollPercentageRef.current = percentage;
        }
      }, 200); // 200ms debounce as requested
    },
    [],
  );

  return (
    <DwellTimingContext.Provider value={{ markApplyStart, handleScroll, setSectionVisibility }}>
      {children}
    </DwellTimingContext.Provider>
  );
}

export function useDwellTiming(): DwellTimingContextValue {
  const context = useContext(DwellTimingContext);
  if (!context) {
    throw new Error("useDwellTiming must be used within a DwellTimingProvider");
  }
  return context;
}
